import { useCallback, useState } from 'react';
import { Alert, Box, Link, Skeleton, Stack } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import type { AcceptRequest, ChargeView, TenantAddonView } from '@/api/generated';
import { resellerBillingApi } from '@/api/resellerBilling';
import { useIsOwner } from '@/auth/authHooks';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useActionError } from '@/components/useActionError';
import { moneyIn } from '@/components/moneyIn';
import { PageHeader } from '@/components/PageHeader';
import { useRemote } from '@/components/useRemote';
import { useToast } from '@/components/useToast';
import { useLocale } from '@/i18n/useLocale';
import { useTenantCurrency } from '@/tenant/useTenant';
import { AddonAcceptDialog } from './AddonAcceptDialog';
import { AddonTable } from './AddonTable';
import { ChargeHistory } from './ChargeHistory';
import { UpdateCardDialog } from './UpdateCardDialog';
import { useAddons } from './useAddons';

interface RetryTarget {
  addon: TenantAddonView;
  charge: ChargeView;
}

export function AddonsScreen() {
  const { t } = useTranslation();
  const showToast = useToast();
  const describe = useActionError();
  const { status, data, error, reload, accept, decline, cancel, replaceCard, retryCharge } =
    useAddons();
  const isOwner = useIsOwner();
  const loadCard = useCallback(() => resellerBillingApi.getCard(), []);
  const card = useRemote(loadCard);
  const [accepting, setAccepting] = useState<TenantAddonView | null>(null);
  const [updating, setUpdating] = useState<TenantAddonView | null>(null);
  const [lastBilling, setLastBilling] = useState<AcceptRequest['billingMode'] | null>(null);
  // The last target stays set after closing, so the dialog keeps its title while it fades out.
  const [confirmRetry, setConfirmRetry] = useState<RetryTarget | null>(null);
  const [retryOpen, setRetryOpen] = useState(false);
  const [retryingChargeId, setRetryingChargeId] = useState<number | null>(null);
  const locale = useLocale();
  const currency = useTenantCurrency();
  const addons = data?.addons ?? [];
  const acceptingNow = accepting
    ? (addons.find((addon) => addon.id === accepting.id) ?? accepting)
    : null;
  const netted = addons.some((addon) => addon.billingMode === 'NET_FROM_PAYOUTS');
  const showCardPrompt = netted && card.status === 'ready' && card.data === null;

  function requestRetry(addon: TenantAddonView, charge: ChargeView) {
    setConfirmRetry({ addon, charge });
    setRetryOpen(true);
  }

  async function runRetry() {
    const addonId = confirmRetry?.addon.id;
    const chargeId = confirmRetry?.charge.id;
    if (addonId === undefined || chargeId === undefined) return;
    setRetryingChargeId(chargeId);
    try {
      await retryCharge(addonId, chargeId);
      showToast(t('addons.toast.retried'));
    } finally {
      setRetryingChargeId(null);
    }
  }

  function retryTitle(target: RetryTarget): string {
    const addon = target.addon.name;
    // A blank tenant currency: the no-amount copy — never a dash inside the sentence.
    if (!currency) {
      return t('addons.retryConfirm.titleNoAmount', { addon });
    }
    const amount = moneyIn(target.charge.amountMinor, currency, locale, t('addons.unavailable'));
    return t('addons.retryConfirm.title', { addon, amount });
  }

  return (
    <Box>
      <PageHeader id="addons" />
      {status === 'loading' && !data ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={280} />
        </Box>
      ) : null}
      {status === 'error' ? <ApiErrorState what={t('errors.what.addons')} error={error} onRetry={reload} /> : null}
      {data ? (
        <Stack spacing={3}>
          {showCardPrompt ? (
            <Alert severity="warning">
              <Trans
                i18nKey="addons.nettedCardPrompt"
                components={{
                  billing: isOwner ? (
                    <Link component={RouterLink} to="/billing" />
                  ) : (
                    <span />
                  ),
                }}
              />
            </Alert>
          ) : null}
          <AddonTable
            addons={addons}
            busy={false}
            onAccept={setAccepting}
            onDecline={async (id) => {
              await decline(id);
            }}
            onCancel={async (id) => {
              await cancel(id);
            }}
            onUpdateCard={setUpdating}
            onRetry={requestRetry}
            retryingChargeId={retryingChargeId}
          />
          <ChargeHistory
            addons={addons}
            onRetry={requestRetry}
            retryingChargeId={retryingChargeId}
          />
        </Stack>
      ) : null}
      <ConfirmDialog
        open={retryOpen}
        title={confirmRetry ? retryTitle(confirmRetry) : ''}
        consequence={t('addons.retryConfirm.body')}
        confirmLabel={t('addons.retryConfirm.confirm')}
        onConfirm={runRetry}
        onCancel={() => setRetryOpen(false)}
        errorMessage={(err) => describe(err).message}
      />
      <AddonAcceptDialog
        addon={acceptingNow}
        lastBilling={lastBilling}
        onClose={() => setAccepting(null)}
        onAccept={async (id, body) => {
          await accept(id, body);
          showToast(t('addons.toast.accepted'));
        }}
        onRememberBilling={setLastBilling}
      />
      <UpdateCardDialog
        addon={updating}
        onClose={() => setUpdating(null)}
        onReplace={async (id, body) => {
          await replaceCard(id, body);
          showToast(t('addons.toast.cardUpdated'));
        }}
      />
    </Box>
  );
}
